import { Box, css, styled, Typography } from '@mui/material'
import { sanitize } from 'dompurify'
import type { FC, RefCallback } from 'react'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { unit } from '@hcr/pwa/components'
import { isNull } from '@hcr/utils'

import { isPwaPropValid } from '../../../utils'

interface ExpandableTextProps {
  lineClamp?: number
  value: string
}

export const ExpandableText: FC<ExpandableTextProps> = ({ lineClamp = 4, value }) => {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)
  const [hasEllipsis, setHasEllipsis] = useState(false)

  const contentRef = useCallback<RefCallback<HTMLSpanElement>>(element => {
    if (isNull(element)) {
      return
    }

    setHasEllipsis(element.offsetHeight < element.scrollHeight)
  }, [])

  return (
    <Container>
      <Content
        lineClamp={lineClamp}
        isExpanded={isExpanded}
        ref={contentRef}
        variant='bodyM'
        dangerouslySetInnerHTML={{ __html: sanitize(value) }}
      />
      {hasEllipsis && (
        <Typography variant='textLinkM' onClick={() => setIsExpanded(!isExpanded)}>
          {isExpanded ? t('expandable-text.read-less') : t('expandable-text.read-more')}
        </Typography>
      )}
    </Container>
  )
}

const Container = styled(Box)`
  display: flex;
  flex-wrap: wrap;
`

const Content = styled(Typography, { shouldForwardProp: isPwaPropValid })<{
  readonly isExpanded: boolean
  readonly lineClamp: number
}>`
  line-height: ${unit(6)};
  word-break: break-word;
  ${props =>
    !props.isExpanded &&
    css`
      display: -webkit-box;
      -webkit-line-break: after-white-space;
      -webkit-line-clamp: ${props.lineClamp};
      -webkit-box-orient: vertical;
      overflow: hidden;
      max-height: calc(
        ${props.lineClamp} * ${unit(6)}
      ); // fallback cut-off for Safari if it doesn't recognize -webkit-line-clamp
    `}
`
